import React, { useEffect, useMemo, useState } from "react";
import { api, resolveAvatarUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EmployeeDrawer from "@/components/EmployeeDrawer";
import { Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Directory() {
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const departments = useMemo(() => {
    const set = new Set(employees.map((e) => e.department).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [employees]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return employees.filter((e) => {
      if (dept !== "all" && e.department !== dept) return false;
      if (!ql) return true;
      return (
        e.name?.toLowerCase().includes(ql) ||
        e.designation?.toLowerCase().includes(ql) ||
        e.email?.toLowerCase().includes(ql) ||
        e.department?.toLowerCase().includes(ql)
      );
    });
  }, [employees, q, dept]);

  const handleSync = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/microsoft/sync");
      toast.success(`Synced ${data.synced}${data.mocked ? " (mock)" : ""}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sync failed", { duration: 8000 });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen p-8 md:p-12" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            People · {filtered.length}
          </p>
          <h1 className="mt-2 text-4xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            Directory
          </h1>
        </div>
        <Button onClick={handleSync} disabled={loading} variant="outline" className="rounded-none border-neutral-300" data-testid="directory-sync-button">
          <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
          Sync from Microsoft
        </Button>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search name, role, department…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="rounded-none border-neutral-300 pl-9"
            data-testid="directory-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {departments.map((d) => (
            <button
              key={d}
              onClick={() => setDept(d)}
              data-testid={`filter-dept-${d}`}
              className={`border px-3 py-1.5 text-[11px] uppercase tracking-wider transition-colors ${
                dept === d ? "border-black bg-black text-white" : "border-neutral-300 text-neutral-700 hover:border-black"
              }`}
              style={{ fontFamily: '"IBM Plex Mono", monospace' }}
            >
              {d === "all" ? "All" : d}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white">
        {filtered.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-neutral-600">No employees match.</p>
        ) : (
          <ul>
            {filtered.map((e) => {
              const avatar = resolveAvatarUrl(e);
              return (
                <li
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className="flex cursor-pointer items-center gap-4 border-b border-neutral-100 px-6 py-3 transition-colors last:border-0 hover:bg-neutral-50"
                  data-testid={`directory-row-${e.id}`}
                >
                  {avatar ? (
                    <img src={avatar} alt="" className="h-9 w-9 object-cover" />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center bg-[#002FA7] text-[10px] text-white">
                      {(e.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{e.name}</p>
                    <p className="truncate text-xs text-neutral-600">{e.designation} · {e.department}</p>
                  </div>
                  <p className="hidden truncate text-xs text-neutral-500 md:block">{e.email}</p>
                  <Badge
                    variant="outline"
                    className={`rounded-none border-neutral-300 text-[10px] uppercase ${
                      e.source === "microsoft" ? "text-[#0A7029]" : "text-neutral-700"
                    }`}
                    style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                  >
                    {e.source}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <EmployeeDrawer
        employee={selected}
        employees={employees}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        onDeleted={() => { setSelected(null); load(); }}
      />
    </div>
  );
}
